import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { forwardRef } from 'react'
import { ChevronDown, CircleAlert, CircleCheck, CircleX, Info } from 'lucide-react'
import { cn } from '@/lib/utils'

export const controlClass = (invalid?: boolean, className?: string): string =>
    cn(
        'w-full rounded-xl border bg-[#0c1714] px-4 text-[15px] text-foreground placeholder:text-muted-foreground/60 transition-colors focus:outline-none focus:border-gold/70 focus:ring-2 focus:ring-gold/30 disabled:opacity-60',
        invalid ? 'border-danger/80 ring-1 ring-danger/35 pe-11' : 'border-input hover:border-gold/35',
        className
    )

interface FieldProps {
    id: string
    label: ReactNode
    error?: string | null
    hint?: ReactNode
    className?: string
    showIcon?: boolean
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ id, label, error, hint, className, showIcon = true, children }): ReactNode => {
    return (
        <div className={cn('space-y-1.5', className)}>
            <label htmlFor={id} className='block text-sm font-medium text-foreground/90'>
                {label}
            </label>

            <div className='relative'>
                {children}

                {error && showIcon && (
                    <CircleX className='pointer-events-none absolute end-3.5 top-1/2 size-5 -translate-y-1/2 text-danger' aria-hidden='true' />
                )}
            </div>

            {error ? <FieldError id={`${id}-error`} message={error} /> : hint ? <p id={`${id}-hint`} className='text-xs leading-relaxed text-muted-foreground'>{hint}</p> : null}
        </div>
    )
}

export const FieldError: FC<{ id?: string, message: string, className?: string }> = ({ id, message, className }): ReactNode => {
    return (
        <p id={id} role='alert' className={cn('flex items-start gap-1.5 text-[13px] font-medium leading-snug text-danger', className)}>
            <CircleX className='mt-px size-4 shrink-0' aria-hidden='true' />
            <span>{message}</span>
        </p>
    )
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
    invalid?: boolean
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({ invalid, className, id, ...rest }, ref) => (
    <input
        ref={ref}
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && id ? `${id}-error` : undefined}
        className={controlClass(invalid, cn('h-12', className))}
        {...rest}
    />
))

TextInput.displayName = 'TextInput'

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    invalid?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({ invalid, className, id, ...rest }, ref) => (
    <textarea
        ref={ref}
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && id ? `${id}-error` : undefined}
        className={controlClass(invalid, cn('min-h-28 py-3 leading-relaxed', className))}
        {...rest}
    />
))

TextArea.displayName = 'TextArea'

interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
    invalid?: boolean
    wrapperClassName?: string
}

// Renders its own chevron and error icon, so use it inside a Field with showIcon={false}.
export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(({ invalid, className, wrapperClassName, id, children, ...rest }, ref) => (
    <div className={cn('relative', wrapperClassName)}>
        <select
            ref={ref}
            id={id}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid && id ? `${id}-error` : undefined}
            className={controlClass(invalid, cn('h-12 cursor-pointer appearance-none pe-10', className))}
            {...rest}
        >
            {children}
        </select>

        {invalid
            ? <CircleX className='pointer-events-none absolute end-3.5 top-1/2 size-5 -translate-y-1/2 text-danger' aria-hidden='true' />
            : <ChevronDown className='pointer-events-none absolute end-3.5 top-1/2 size-4 -translate-y-1/2 text-gold' aria-hidden='true' />}
    </div>
))

SelectInput.displayName = 'SelectInput'

type NoticeTone = 'error' | 'success' | 'info' | 'warning'

const NOTICE_STYLES: Record<NoticeTone, string> = {
    error: 'border-danger/45 bg-[#2a1214] text-[#ffb4b4]',
    success: 'border-success/45 bg-[#0f2a1b] text-[#a7f3c4]',
    info: 'border-gold/35 bg-[#1d2318] text-[#f1e2b4]',
    warning: 'border-[#f0a33a]/45 bg-[#2a1f0f] text-[#ffd69a]'
}

const NOTICE_ICONS = {
    error: CircleX,
    success: CircleCheck,
    info: Info,
    warning: CircleAlert
}

export const Notice: FC<{ tone?: NoticeTone, title?: ReactNode, children?: ReactNode, className?: string }> = ({ tone = 'info', title, children, className }): ReactNode => {
    const Icon = NOTICE_ICONS[tone]

    return (
        <div role={tone === 'error' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-2xl border px-4 py-3.5 text-sm leading-relaxed', NOTICE_STYLES[tone], className)}>
            <Icon className='mt-0.5 size-5 shrink-0' aria-hidden='true' />

            <div className='space-y-1'>
                {title && <p className='font-semibold'>{title}</p>}
                {children && <div>{children}</div>}
            </div>
        </div>
    )
}
